import React, { useEffect, useRef, useState } from 'react';
import { usePatientRegister } from '../hooks/usePatientRegister';
import DatePickerInput from '../../../components/ui/DatePickerInput';

const PatientRegister: React.FC = () => {
  // E.1: transient microcopy hints when a parent in the geographic cascade
  // changes between non-empty values. Each hint auto-clears after 3 s and
  // the cleanup() pattern keeps us safe on unmount.
  const [showMunicipalityHint, setShowMunicipalityHint] = useState(false);
  const [showDistrictHint, setShowDistrictHint] = useState(false);
  const prevProvinceIdRef = useRef<number | null>(null);
  const prevMunicipalityIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!showMunicipalityHint) return;
    const t = setTimeout(() => setShowMunicipalityHint(false), 3000);
    return () => clearTimeout(t);
  }, [showMunicipalityHint]);

  useEffect(() => {
    if (!showDistrictHint) return;
    const t = setTimeout(() => setShowDistrictHint(false), 3000);
    return () => clearTimeout(t);
  }, [showDistrictHint]);

  const {
    formData,
    errors,
    loading,
    catalogsLoading,
    catalogsError,
    formError,
    successMessage,
    insuranceForm,
    nationalities,
    provinces,
    municipalities,
    districts,
    identificationTypes,
    insuranceCompanies,
    insurancePlans,
    selectedMunicipalityId,
    districtsLoading,
    selectedInsuranceCompanyId,
    insurancePlansLoading,
    handleSubmit,
    handleInputChange,
    handleMunicipalitySelect,
    handleDistrictChange,
    handleInsuranceCompanySelect,
    addInsurance,
    removeInsurance,
    clearForm,
    setInsuranceForm,
  } = usePatientRegister();

  const selectedIdType = identificationTypes.find(
    (type) => type.id === formData.identificationType
  );
  const isCedula =
    selectedIdType?.name === 'Cédula' ||
    (selectedIdType == null && formData.identificationType === 1);

  const controlClass = (hasError?: string) =>
    `form-control form-control-sm${hasError ? ' error' : ''}`;
  const selectClass = (hasError?: string) =>
    `form-select form-select-sm${hasError ? ' error' : ''}`;

  return (
    <div className="row">
      <div className="col-12 col-xl-10">
        <form
          onSubmit={handleSubmit}
          noValidate
          aria-label="Registro de Paciente"
        >
          {successMessage && (
            <div className="alert alert-success" role="status">{successMessage}</div>
          )}
          {formError && (
            <div className="alert alert-danger" role="alert">{formError}</div>
          )}
          {catalogsError && (
            <div className="alert alert-warning" role="status">{catalogsError}</div>
          )}

          <fieldset className="border rounded px-3 pb-3 pt-1 mb-3 bg-white">
            <legend className="float-none w-auto px-2 fs-6 fw-semibold mb-0">Identidad</legend>
            <div className="row g-3 mt-1">
              <div className="col-md-6">
                <label htmlFor="firstName" className="form-label form-label-sm">Nombre</label>
                <input
                  id="firstName"
                  type="text"
                  autoComplete="given-name"
                  className={controlClass(errors.firstName)}
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  disabled={loading}
                />
                {errors.firstName && <span className="error-message">{errors.firstName}</span>}
              </div>
              <div className="col-md-6">
                <label htmlFor="lastName" className="form-label form-label-sm">Apellido</label>
                <input
                  id="lastName"
                  type="text"
                  autoComplete="family-name"
                  className={controlClass(errors.lastName)}
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  disabled={loading}
                />
                {errors.lastName && <span className="error-message">{errors.lastName}</span>}
              </div>

              <div className="col-md-6">
                <label htmlFor="identificationType" className="form-label form-label-sm">
                  Tipo de identificación
                </label>
                <div className="position-relative">
                  <select
                    id="identificationType"
                    className={selectClass(errors.identificationType)}
                    value={
                      identificationTypes.some((type) => type.id === formData.identificationType)
                        ? formData.identificationType
                        : ''
                    }
                    onChange={(e) => {
                      const value = e.target.value;
                      handleInputChange(
                        'identificationType',
                        value ? parseInt(value, 10) : 0
                      );
                    }}
                    disabled={loading || catalogsLoading}
                  >
                    <option value="">Seleccionar</option>
                    {identificationTypes.map((type) => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                  {errors.identificationType && (
                    <span className="error-message">{errors.identificationType}</span>
                  )}
                  {catalogsLoading && (
                    <span
                      className="spinner-border spinner-border-sm position-absolute top-50 end-0 translate-middle-y me-2"
                      role="status"
                      aria-hidden="true"
                    />
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <label htmlFor="identification" className="form-label form-label-sm">
                  Identificación
                </label>
                <input
                  id="identification"
                  type="text"
                  inputMode={isCedula ? 'numeric' : 'text'}
                  className={controlClass(errors.identification)}
                  placeholder={isCedula ? '000-0000000-0' : 'Número de identificación'}
                  value={formData.identification}
                  onChange={(e) => handleInputChange('identification', e.target.value)}
                  disabled={loading}
                />
                {errors.identification && (
                  <span className="error-message">{errors.identification}</span>
                )}
                {isCedula && !errors.identification && (
                  <small className="form-text text-muted">
                    Formato 000-0000000-0 con dígito verificador.
                  </small>
                )}
              </div>

              <div className="col-md-6">
                <span className="form-label form-label-sm d-block" id="gender-label">Género</span>
                <div className="d-flex flex-wrap gap-3 pt-1" role="group" aria-labelledby="gender-label">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="gender"
                      id="gender-male"
                      value="true"
                      aria-label="Masculino"
                      checked={formData.gender}
                      onChange={() => handleInputChange('gender', true)}
                      disabled={loading}
                    />
                    <label className="form-check-label" htmlFor="gender-male">Masculino</label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="gender"
                      id="gender-female"
                      value="false"
                      aria-label="Femenino"
                      checked={!formData.gender}
                      onChange={() => handleInputChange('gender', false)}
                      disabled={loading}
                    />
                    <label className="form-check-label" htmlFor="gender-female">Femenino</label>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <label htmlFor="birthDate" className="form-label form-label-sm">
                  Fecha de nacimiento
                </label>
                <DatePickerInput
                  id="birthDate"
                  className={controlClass(errors.birthDate)}
                  value={formData.birthDate}
                  onChange={(value) => handleInputChange('birthDate', value)}
                  disabled={loading}
                />
                {errors.birthDate && <span className="error-message">{errors.birthDate}</span>}
              </div>

              <div className="col-md-6">
                <label htmlFor="recordNumber" className="form-label form-label-sm">
                  Número de expediente
                </label>
                <input
                  id="recordNumber"
                  type="text"
                  className={controlClass(errors.recordNumber)}
                  placeholder="0000-0000A"
                  maxLength={10}
                  value={formData.recordNumber}
                  onChange={(e) => handleInputChange('recordNumber', e.target.value)}
                  disabled={loading}
                />
                {errors.recordNumber && (
                  <span className="error-message">{errors.recordNumber}</span>
                )}
              </div>
              <div className="col-md-6 d-flex align-items-end">
                <div className="form-check mb-2">
                  <input
                    id="isActive"
                    className="form-check-input"
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => handleInputChange('isActive', e.target.checked)}
                    disabled={loading}
                  />
                  <label htmlFor="isActive" className="form-check-label">Activo</label>
                </div>
              </div>
            </div>
          </fieldset>

          <fieldset className="border rounded px-3 pb-3 pt-1 mb-3 bg-white">
            <legend className="float-none w-auto px-2 fs-6 fw-semibold mb-0">Contacto</legend>
            <div className="row g-3 mt-1">
              <div className="col-md-6">
                <label htmlFor="email" className="form-label form-label-sm">Correo electrónico</label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  className={controlClass(errors.email)}
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  disabled={loading}
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>
              <div className="col-md-6">
                <label htmlFor="phone" className="form-label form-label-sm">Teléfono</label>
                <input
                  id="phone"
                  type="tel"
                  autoComplete="tel"
                  className="form-control form-control-sm"
                  placeholder="(999) 999-9999"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  disabled={loading}
                />
              </div>
              <div className="col-md-6">
                <label htmlFor="mobile" className="form-label form-label-sm">Celular</label>
                <input
                  id="mobile"
                  type="tel"
                  autoComplete="tel"
                  className="form-control form-control-sm"
                  placeholder="(999) 999-9999"
                  value={formData.mobile || ''}
                  onChange={(e) => handleInputChange('mobile', e.target.value)}
                  disabled={loading}
                />
              </div>
              <div className="col-md-6">
                <span className="form-label form-label-sm d-block">WhatsApp</span>
                <div className="form-check mt-1">
                  <input
                    id="whatsappOptIn"
                    className="form-check-input"
                    type="checkbox"
                    checked={formData.whatsappOptIn || false}
                    onChange={(e) => handleInputChange('whatsappOptIn', e.target.checked)}
                    disabled={loading}
                  />
                  <label className="form-check-label" htmlFor="whatsappOptIn">
                    Recibir recordatorios por WhatsApp
                  </label>
                </div>
              </div>
              <div className="col-md-6">
                <label htmlFor="whatsappPhone" className="form-label form-label-sm">
                  Teléfono WhatsApp
                </label>
                <input
                  id="whatsappPhone"
                  type="tel"
                  className="form-control form-control-sm"
                  placeholder="(999) 999-9999"
                  value={formData.whatsappPhone || formData.mobile || ''}
                  onChange={(e) => handleInputChange('whatsappPhone', e.target.value)}
                  disabled={loading}
                />
              </div>
              <div className="col-12">
                <label htmlFor="address" className="form-label form-label-sm">Dirección</label>
                <input
                  id="address"
                  type="text"
                  autoComplete="street-address"
                  className={controlClass(errors.address)}
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  disabled={loading}
                />
                {errors.address && <span className="error-message">{errors.address}</span>}
              </div>
            </div>
          </fieldset>

          <fieldset className="border rounded px-3 pb-3 pt-1 mb-3 bg-white">
            <legend className="float-none w-auto px-2 fs-6 fw-semibold mb-0">Ubicación</legend>
            <div className="row g-3 mt-1">
              <div className="col-md-6">
                <label htmlFor="nationalityId" className="form-label form-label-sm">Nacionalidad</label>
                <select
                  id="nationalityId"
                  className={selectClass(errors.nationalityId)}
                  value={formData.nationalityId}
                  onChange={(e) => handleInputChange('nationalityId', parseInt(e.target.value, 10))}
                  disabled={loading || catalogsLoading}
                >
                  <option value={0}>Seleccionar</option>
                  {nationalities.map((nat) => (
                    <option key={nat.id} value={nat.id}>{nat.name}</option>
                  ))}
                </select>
                {errors.nationalityId && (
                  <span className="error-message">{errors.nationalityId}</span>
                )}
              </div>
              <div className="col-md-6">
                <label htmlFor="province" className="form-label form-label-sm">Provincia</label>
                <select
                  id="province"
                  className={selectClass(errors.provinceId)}
                  value={formData.provinceId ?? ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    const newId = value ? parseInt(value, 10) : null;
                    if (
                      prevProvinceIdRef.current != null &&
                      newId != null &&
                      prevProvinceIdRef.current !== newId
                    ) {
                      setShowMunicipalityHint(true);
                    }
                    prevProvinceIdRef.current = newId;
                    handleInputChange('provinceId', newId);
                  }}
                  disabled={loading || catalogsLoading}
                >
                  <option value="">Seleccionar</option>
                  {provinces.map((prov) => (
                    <option key={prov.id} value={prov.id}>{prov.name}</option>
                  ))}
                </select>
                {errors.provinceId && (
                  <span className="error-message">{errors.provinceId}</span>
                )}
              </div>
              <div className="col-md-6">
                <label htmlFor="municipality" className="form-label form-label-sm">Municipio</label>
                <select
                  id="municipality"
                  className="form-select form-select-sm"
                  value={selectedMunicipalityId ?? ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    const newId = value ? parseInt(value, 10) : null;
                    if (
                      prevMunicipalityIdRef.current != null &&
                      newId != null &&
                      prevMunicipalityIdRef.current !== newId
                    ) {
                      setShowDistrictHint(true);
                    }
                    prevMunicipalityIdRef.current = newId;
                    handleMunicipalitySelect(newId);
                  }}
                  disabled={loading || !formData.provinceId}
                >
                  <option value="">Seleccionar</option>
                  {municipalities.map((mun) => (
                    <option key={mun.id} value={mun.id}>{mun.name}</option>
                  ))}
                </select>
                {showMunicipalityHint && (
                  <small className="form-text text-muted">Selecciona un municipio</small>
                )}
              </div>
              <div className="col-md-6">
                <label htmlFor="district" className="form-label form-label-sm">Distrito</label>
                <select
                  id="district"
                  className={selectClass(errors.municipalDistrictId)}
                  value={formData.municipalDistrictId ?? ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleDistrictChange(value ? parseInt(value, 10) : null);
                  }}
                  disabled={loading || !selectedMunicipalityId}
                >
                  <option value="">Seleccionar</option>
                  {districts.map((dist) => (
                    <option key={dist.id} value={dist.id}>{dist.name}</option>
                  ))}
                </select>
                {errors.municipalDistrictId && (
                  <span className="error-message">{errors.municipalDistrictId}</span>
                )}
                {selectedMunicipalityId != null &&
                  !districtsLoading &&
                  districts.length === 0 && (
                  <small className="form-text text-muted">
                    No hay distritos para este municipio. Puede registrar el paciente sin distrito.
                  </small>
                )}
                {showDistrictHint && (
                  <small className="form-text text-muted">Selecciona un distrito</small>
                )}
              </div>
            </div>
          </fieldset>

          <fieldset className="border rounded px-3 pb-3 pt-1 mb-3 bg-white">
            <legend className="float-none w-auto px-2 fs-6 fw-semibold mb-0">Seguros</legend>
            <p className="text-muted small mb-3 mt-2">
              Opcional. Puede registrar el paciente sin póliza.
            </p>
            <div className="row g-3">
              <div className="col-md-6">
                <label htmlFor="insuranceCompany" className="form-label form-label-sm">Aseguradora</label>
                <select
                  id="insuranceCompany"
                  className="form-select form-select-sm"
                  value={selectedInsuranceCompanyId ?? ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleInsuranceCompanySelect(value ? parseInt(value, 10) : null);
                  }}
                  disabled={loading || catalogsLoading}
                >
                  <option value="">Seleccionar</option>
                  {insuranceCompanies.map((company) => (
                    <option key={company.id} value={company.id}>{company.name}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label htmlFor="insurancePlan" className="form-label form-label-sm">Plan</label>
                <select
                  id="insurancePlan"
                  className="form-select form-select-sm"
                  value={insuranceForm.insurancePlan?.id || ''}
                  onChange={(e) => {
                    const planId = parseInt(e.target.value, 10);
                    const plan = insurancePlans.find((p) => p.id === planId);
                    const company = insuranceCompanies.find(
                      (c) => c.id === selectedInsuranceCompanyId
                    );
                    setInsuranceForm((prev) => ({
                      ...prev,
                      insurancePlan: plan
                        ? {
                            id: plan.id,
                            insurance: {
                              name: plan.company_name || company?.name || 'ARS',
                              description: '',
                            },
                            plan: { name: plan.name },
                          }
                        : undefined,
                    }));
                  }}
                  disabled={loading || selectedInsuranceCompanyId == null || insurancePlansLoading}
                >
                  <option value="">Seleccionar</option>
                  {insurancePlans.map((plan) => (
                    <option key={plan.id} value={plan.id}>{plan.name}</option>
                  ))}
                </select>
                {selectedInsuranceCompanyId == null && (
                  <small className="form-text text-muted">Seleccione la aseguradora</small>
                )}
                {selectedInsuranceCompanyId != null &&
                  !insurancePlansLoading &&
                  insurancePlans.length === 0 && (
                  <small className="form-text text-muted">
                    No hay planes para esta aseguradora.
                  </small>
                )}
              </div>
              <div className="col-md-6">
                <label htmlFor="policyNumber" className="form-label form-label-sm">Póliza</label>
                <input
                  id="policyNumber"
                  type="text"
                  className="form-control form-control-sm"
                  value={insuranceForm.policyNumber || ''}
                  onChange={(e) => setInsuranceForm((prev) => ({ ...prev, policyNumber: e.target.value }))}
                  disabled={loading}
                />
              </div>
              <div className="col-md-6">
                <label htmlFor="nss" className="form-label form-label-sm">NSS</label>
                <input
                  id="nss"
                  type="text"
                  className="form-control form-control-sm"
                  value={insuranceForm.nss || ''}
                  onChange={(e) => setInsuranceForm((prev) => ({ ...prev, nss: e.target.value }))}
                  disabled={loading}
                />
              </div>
              <div className="col-md-6">
                <label htmlFor="cardNumber" className="form-label form-label-sm">Carnet</label>
                <input
                  id="cardNumber"
                  type="text"
                  className="form-control form-control-sm"
                  value={insuranceForm.cardNumber || ''}
                  onChange={(e) => setInsuranceForm((prev) => ({ ...prev, cardNumber: e.target.value }))}
                  disabled={loading}
                />
              </div>
              <div className="col-12">
                <button type="button" className="btn btn-sm btn-primary" onClick={addInsurance} disabled={loading}>
                  <i className="fa fa-plus-circle me-1" /> Agregar
                </button>
              </div>
            </div>

            <div className="list-group mt-3">
              {(formData.firstName || formData.lastName) && (
                <div className="list-group-item">
                  Seguros del paciente -{' '}
                  <b className="text text-primary">
                    {formData.firstName} {formData.lastName}
                  </b>
                </div>
              )}
              {formData.insurances.map((insurance, index) => (
                <div key={index} className="list-group-item">
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div>
                      <h4 className="h6 mb-1">
                        {insurance.insurancePlan.insurance.name} {insurance.insurancePlan.plan.name}
                      </h4>
                      <p className="mb-1">
                        <b>Póliza:</b> {insurance.policyNumber} <b>NSS:</b> {insurance.nss}
                      </p>
                      <p className="list-group-item-text mb-0">
                        {insurance.insurancePlan.insurance.description}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={() => removeInsurance(index)}
                      disabled={loading}
                    >
                      <i className="fa fa-times" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </fieldset>

          <div className="d-flex justify-content-end gap-2 mt-3 sticky-bottom bg-white py-3 border-top">
            <button type="button" className="btn btn-outline-secondary" onClick={clearForm} disabled={loading}>
              <i className="fa fa-times me-1" /> Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  Guardando...
                </>
              ) : (
                <>
                  <i className="fa fa-save me-1" /> Registrar paciente
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PatientRegister;
